import { FormControl, Grid, Typography } from '@mui/material';

import { DatePicker } from '@components/datePicker';
import { isValid } from 'date-fns';
import { useEffect, useState } from 'react';
import { DateMinMax } from '@components/table/types/DateMinMax.type';

export type DateRangePickerProps = {
  onMinChange: (value: Date | null, maxDate: Date | null) => void;
  onMaxChange: (value: Date | null, minDate: Date | null) => void;
  label?: string;
  disabled?: boolean;
  value?: DateMinMax;
  defaultValue?: DateMinMax;
  displayMode?: 'horizontal' | 'vertical';
};

export const ensureValueIsADate = (value?: string | Date | null) => {
  if (!value) return null;

  const d = new Date(value);
  if (isValid(d)) {
    return d;
  }

  return null;
};

export const DateRangePicker = ({
  onMinChange,
  onMaxChange,
  label,
  disabled,
  value,
  defaultValue,
  displayMode = 'vertical',
}: DateRangePickerProps) => {
  const [stateValue, setStateValue] = useState({
    min: ensureValueIsADate(value?.min) ?? ensureValueIsADate(defaultValue?.min) ?? null,
    max: ensureValueIsADate(value?.max) ?? ensureValueIsADate(defaultValue?.max) ?? null,
  });

  const isHorizontal = displayMode === 'horizontal';

  const width = isHorizontal ? 'auto' : 180;

  useEffect(() => {
    setStateValue({
      min: ensureValueIsADate(value?.min) ?? null,
      max: ensureValueIsADate(value?.max) ?? null,
    });
  }, [value]);

  return (
    <Grid
      container
      size={{ xs: 12 }}
      sx={{
        padding: 0,
        alignItems: isHorizontal ? 'center' : 'flex-start',
        flexDirection: isHorizontal ? 'row' : 'column',
        gap: isHorizontal ? '4px' : '0',

        '.clearButton': {
          width: 24,
        },
      }}
    >
      <Grid sx={{ flex: 2 }}>
        <FormControl>
          {label && (
            <Typography variant='caption' color='text.main' sx={{ mb: 0.7 }}>
              {label}
            </Typography>
          )}

          <Grid container>
            <Grid sx={{ width }}>
              <DatePicker
                value={stateValue ? stateValue.min : null}
                onChange={(e) => {
                  const isValidDate = e ? isValid(e) : false;
                  if (isValidDate) {
                    setStateValue((cur) => ({ min: e ?? null, max: cur?.max ?? null }));
                    onMinChange(e, stateValue?.max ?? null);
                  }
                }}
                disabled={disabled}
                maxDate={value?.max && isValid(value.max) ? new Date(value.max) : undefined}
                textFieldProps={{
                  error: false,

                  onBlur: (e) => {
                    const value = e.target.value;
                    const isValidDate = value ? isValid(new Date(value)) : false;

                    if (!isValidDate) {
                      setStateValue((cur) => ({ min: null, max: cur?.max ?? null }));
                      onMinChange(null, stateValue?.max ?? null);
                    } else {
                      // ensure date is before the max date
                      if (stateValue?.max && e.target.value) {
                        const minDate = new Date(e.target.value);
                        const maxDate = new Date(stateValue.max);
                        if (minDate > maxDate) {
                          setStateValue((cur) => ({ min: null, max: cur?.max ?? null }));
                          onMinChange(null, stateValue?.max ?? null);
                        }
                      }
                    }
                  },
                }}
                fullWidth
                clearable
              />
            </Grid>
          </Grid>
        </FormControl>
      </Grid>
      <Grid>
        <Typography variant='caption'>To</Typography>
      </Grid>

      <Grid sx={{ flex: 2 }}>
        <FormControl>
          <Grid
            container
            sx={{
              flexDirection: 'row',
              gap: 1,
              alignItems: 'flex-start',
            }}
          >
            <Grid sx={{ width }}>
              <DatePicker
                value={stateValue ? stateValue.max : null}
                onChange={(e) => {
                  const isValidDate = e ? isValid(e) : false;
                  if (isValidDate) {
                    setStateValue((cur) => ({ min: cur?.min ?? null, max: e ?? null }));
                    onMaxChange(e, stateValue?.min ?? null);
                  }
                }}
                disabled={disabled}
                fullWidth
                clearable
                minDate={value?.min && isValid(value.min) ? new Date(value.min) : undefined}
                textFieldProps={{
                  error: false,
                  onBlur: (e) => {
                    const value = e.target.value;
                    const isValidDate = value ? isValid(new Date(value)) : false;
                    if (!isValidDate) {
                      setStateValue((cur) => ({ min: cur?.min ?? null, max: null }));
                      onMaxChange(null, stateValue?.min ?? null);
                    } else {
                      // ensure date is after the min date
                      if (stateValue?.min && e.target.value) {
                        const minDate = new Date(stateValue.min);
                        const maxDate = new Date(e.target.value);
                        if (maxDate < minDate) {
                          setStateValue((cur) => ({ min: cur?.min ?? null, max: null }));
                          onMaxChange(null, stateValue?.min ?? null);
                        }
                      }
                    }
                  },
                }}
              />
            </Grid>
          </Grid>
        </FormControl>
      </Grid>
    </Grid>
  );
};
